import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Grid,
  Group,
  ThemeIcon,
  Table,
  Loader,
  Button,
  Badge,
  ScrollArea,
} from "@mantine/core";
import {
  IconShield,
  IconCalendar,
  IconDownload,
  IconRefresh,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { usePerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { DatePickerInput } from "@mantine/dates";

interface RiskComplianceSummary {
  totalOutstandingBalance: number;
  totalDefaultedAmount: number;
  overdueCount: number;
  defaultedCount: number;
  defaultRate: number;
  highRiskLoansCount: number;
  kycVerificationRate: number;
  bvnVerificationRate: number;
}

interface OverdueLoan {
  loanId: string;
  userId: string;
  userName: string;
  userEmail: string;
  loanProduct: string;
  principal: number;
  totalDue: number;
  amountPaid: number;
  outstandingBalance: number;
  daysOverdue: number;
  createdAt: number;
}

interface HighRiskLoan {
  loanId: string;
  userId: string;
  userName: string;
  userEmail: string;
  loanProduct: string;
  principal: number;
  outstandingBalance: number;
  riskLevel: string;
}

interface RiskComplianceResponse {
  summary: RiskComplianceSummary;
  overdueLoans: OverdueLoan[];
  highRiskLoans: HighRiskLoan[];
  kycStats: {
    verified: number;
    notVerified: number;
    total: number;
  };
  bvnStats: {
    verified: number;
    notVerified: number;
    total: number;
  };
}

export default function RiskComplianceReport() {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate?: number; endDate?: number }>({});

  const {
    data: reportData,
    isLoading,
    reloadData,
  } = usePerformRequest<{ data: RiskComplianceResponse }>({
    method: Endpoints.GetRiskComplianceReport(
      dateRange.startDate,
      dateRange.endDate
    ).method,
    route: Endpoints.GetRiskComplianceReport(
      dateRange.startDate,
      dateRange.endDate
    ).route,
  });

  useEffect(() => {
    const getTimestamp = (date: Date | null | string | unknown): number | undefined => {
      if (!date) return undefined;
      if (date instanceof Date) return date.getTime();
      if (typeof date === 'string') {
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? undefined : parsed.getTime();
      }
      return undefined;
    };
    const start = getTimestamp(startDate);
    const end = getTimestamp(endDate);
    setDateRange({ startDate: start, endDate: end });
  }, [startDate, endDate]);

  useEffect(() => {
    reloadData();
  }, [dateRange]);

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const summary = reportData?.data?.summary;
  const overdueLoans = reportData?.data?.overdueLoans || [];
  const highRiskLoans = reportData?.data?.highRiskLoans || [];
  const kycStats = reportData?.data?.kycStats;
  const bvnStats = reportData?.data?.bvnStats;

  const statCards = summary
    ? [
        {
          label: "Total Outstanding",
          value: formatCurrency(summary.totalOutstandingBalance),
          color: "#DC2626",
        },
        {
          label: "Overdue Loans",
          value: summary.overdueCount.toLocaleString(),
          color: "#EF4444",
        },
        {
          label: "Default Rate",
          value: `${summary.defaultRate.toFixed(2)}%`,
          color: "#F59E0B",
        },
        {
          label: "High-Risk Loans",
          value: summary.highRiskLoansCount.toLocaleString(),
          color: "#F97316",
        },
        {
          label: "KYC Verification",
          value: `${summary.kycVerificationRate.toFixed(2)}%`,
          color: "#10B981",
        },
        {
          label: "BVN Verification",
          value: `${summary.bvnVerificationRate.toFixed(2)}%`,
          color: "#059669",
        },
      ]
    : [];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <ThemeIcon size={48} radius="md" style={{ background: "#DC2626" }}>
              <IconShield size={24} color="white" />
            </ThemeIcon>
            <div>
              <Title order={2} fw={700}>
                Risk & Compliance Report
              </Title>
              <Text size="sm" c="dimmed">
                Monitor loan risks, defaults, and compliance metrics
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => reloadData()}>
              Refresh
            </Button>
            <Button variant="light" leftSection={<IconDownload size={16} />} disabled>
              Export
            </Button>
          </Group>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group gap="md">
            <DatePickerInput
              label="Start Date"
              placeholder="Select start date"
              value={startDate}
              onChange={(value) => setStartDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
            />
            <DatePickerInput
              label="End Date"
              placeholder="Select end date"
              value={endDate}
              onChange={(value) => setEndDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
              minDate={
                startDate
                  ? startDate instanceof Date
                    ? new Date(startDate.getTime() + 86400000)
                    : new Date(new Date(startDate as string).getTime() + 86400000)
                  : undefined
              }
            />
          </Group>
        </Paper>

        {isLoading ? (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Group justify="center">
              <Loader size="lg" />
            </Group>
          </Paper>
        ) : summary ? (
          <>
            <Grid gutter="md">
              {statCards.map((card, index) => (
                <Grid.Col key={card.label} span={{ base: 12, sm: 6, md: 4 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Paper shadow="sm" p="lg" radius="md" withBorder style={{ height: "100%" }}>
                      <Stack gap="xs">
                        <Text size="sm" c="dimmed" fw={500}>
                          {card.label}
                        </Text>
                        <Text size="xl" fw={700} style={{ color: card.color }}>
                          {card.value}
                        </Text>
                      </Stack>
                    </Paper>
                  </motion.div>
                </Grid.Col>
              ))}
            </Grid>

            {/* KYC & BVN Stats */}
            {(kycStats || bvnStats) && (
              <Grid gutter="md">
                {kycStats && (
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Paper shadow="sm" p="lg" radius="md" withBorder>
                      <Title order={4} mb="md">
                        KYC Verification Status
                      </Title>
                      <Stack gap="sm">
                        <Group justify="space-between">
                          <Text>Verified</Text>
                          <Badge color="green" size="lg">
                            {kycStats.verified} ({kycStats.total > 0 ? ((kycStats.verified / kycStats.total) * 100).toFixed(1) : 0}%)
                          </Badge>
                        </Group>
                        <Group justify="space-between">
                          <Text>Not Verified</Text>
                          <Badge color="red" size="lg">
                            {kycStats.notVerified} ({kycStats.total > 0 ? ((kycStats.notVerified / kycStats.total) * 100).toFixed(1) : 0}%)
                          </Badge>
                        </Group>
                        <Group justify="space-between">
                          <Text fw={600}>Total Users</Text>
                          <Text fw={600}>{kycStats.total}</Text>
                        </Group>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                )}
                {bvnStats && (
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Paper shadow="sm" p="lg" radius="md" withBorder>
                      <Title order={4} mb="md">
                        BVN Verification Status
                      </Title>
                      <Stack gap="sm">
                        <Group justify="space-between">
                          <Text>Verified</Text>
                          <Badge color="green" size="lg">
                            {bvnStats.verified} ({bvnStats.total > 0 ? ((bvnStats.verified / bvnStats.total) * 100).toFixed(1) : 0}%)
                          </Badge>
                        </Group>
                        <Group justify="space-between">
                          <Text>Not Verified</Text>
                          <Badge color="red" size="lg">
                            {bvnStats.notVerified} ({bvnStats.total > 0 ? ((bvnStats.notVerified / bvnStats.total) * 100).toFixed(1) : 0}%)
                          </Badge>
                        </Group>
                        <Group justify="space-between">
                          <Text fw={600}>Total Users</Text>
                          <Text fw={600}>{bvnStats.total}</Text>
                        </Group>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                )}
              </Grid>
            )}

            {/* Overdue Loans Table */}
            {overdueLoans.length > 0 && (
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Group justify="space-between" mb="md">
                  <Title order={4}>
                    <Group gap="xs">
                      <IconAlertTriangle size={20} color="#DC2626" />
                      Overdue Loans
                    </Group>
                  </Title>
                  <Badge color="red" size="lg">
                    {overdueLoans.length} loans
                  </Badge>
                </Group>
                <ScrollArea>
                  <Table.ScrollContainer minWidth={800}>
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>User</Table.Th>
                          <Table.Th>Loan Product</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Principal</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Outstanding</Table.Th>
                          <Table.Th>Days Overdue</Table.Th>
                          <Table.Th>Status</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {overdueLoans.map((loan) => (
                          <Table.Tr key={loan.loanId}>
                            <Table.Td>
                              <Stack gap={2}>
                                <Text size="sm" fw={500}>
                                  {loan.userName || "N/A"}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  {loan.userEmail}
                                </Text>
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{loan.loanProduct}</Text>
                            </Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>
                              <Text size="sm" fw={600}>
                                {formatCurrency(loan.principal)}
                              </Text>
                            </Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>
                              <Text size="sm" fw={600} c="red">
                                {formatCurrency(loan.outstandingBalance)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge color="red" variant="light">
                                {loan.daysOverdue} days
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Badge color="red">OVERDUE</Badge>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </ScrollArea>
              </Paper>
            )}

            {/* High-Risk Loans Table */}
            {highRiskLoans.length > 0 && (
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Group justify="space-between" mb="md">
                  <Title order={4}>High-Risk Loans</Title>
                  <Badge color="orange" size="lg">
                    {highRiskLoans.length} loans
                  </Badge>
                </Group>
                <ScrollArea>
                  <Table.ScrollContainer minWidth={700}>
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>User</Table.Th>
                          <Table.Th>Loan Product</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Principal</Table.Th>
                          <Table.Th style={{ textAlign: "right" }}>Outstanding</Table.Th>
                          <Table.Th>Risk Level</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {highRiskLoans.map((loan) => (
                          <Table.Tr key={loan.loanId}>
                            <Table.Td>
                              <Stack gap={2}>
                                <Text size="sm" fw={500}>
                                  {loan.userName || "N/A"}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  {loan.userEmail}
                                </Text>
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{loan.loanProduct}</Text>
                            </Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>
                              <Text size="sm" fw={600}>
                                {formatCurrency(loan.principal)}
                              </Text>
                            </Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>
                              <Text size="sm" fw={600} c="orange">
                                {formatCurrency(loan.outstandingBalance)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge color="orange">{loan.riskLevel.toUpperCase()}</Badge>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </ScrollArea>
              </Paper>
            )}
          </>
        ) : (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Text c="dimmed" ta="center">
              No data available
            </Text>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

